/* eslint-disable @typescript-eslint/no-explicit-any */
import DOMPurify from 'dompurify';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { auth, storage } from './firebase';
import { ensureCrmStorageAccessBeforeUpload } from './crmStorageAccess';
import { buildCommentAttachmentStorageFileName } from './storageFileNames';
import type { CommentMention } from '../types';
import {
  commentHasInlineBoardMention,
  hubHashToken,
  parseHubHashFromElement,
} from './commentHubHashMentions';
import {
  buildStructureMentionToken,
  parseStructureMentionFromElement,
} from './proposalStructureMentions';

export { commentHasInlineBoardMention } from './commentHubHashMentions';

/**
 * Tag allow-list for sanitized comment HTML. Mirrors the TipTap extensions we ship:
 * StarterKit (paragraph/strong/em/strike/code/codeBlock/blockquote/ul/ol/li/h3/hr/br + configured Link),
 */
const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 's', 'code', 'pre', 'mark', 'blockquote',
  'ul', 'ol', 'li', 'h3', 'hr', 'a', 'span', 'img',
];

const ALLOWED_ATTR = [
  'href', 'target', 'rel',
  'data-user-id', 'data-mention', 'data-label', 'data-board', 'data-hub-link', 'data-team',
  'data-structure-id', 'data-deal-id',
  'class', 'src', 'alt', 'title',
];

/**
 * Render-time sanitizer. Forces every link to open in a new tab with `noopener`
 * and strips anything outside the allow-list. Returns sanitized HTML string.
 */
export function sanitizeCommentHtml(html: string): string {
  if (!html) return '';
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick'],
  });
  return clean;
}

/** Policy prose: headings, lists, definition lists, tables, semantic sections (CRM-stored HTML). */
const HANDBOOK_POLICY_TAGS = [
  'p', 'br', 'strong', 'em', 's', 'code', 'pre', 'blockquote',
  'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'hr', 'a',
  'dl', 'dt', 'dd', 'article', 'section', 'header', 'div',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
] as const;

const HANDBOOK_POLICY_ATTR = [
  'href', 'target', 'rel', 'class', 'id', 'aria-labelledby',
  'scope', 'colspan', 'rowspan',
] as const;

/**
 * Sanitize handbook policy body HTML from Firestore before `dangerouslySetInnerHTML`.
 */
export function sanitizeHandbookPolicyHtml(html: string): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...HANDBOOK_POLICY_TAGS],
    ALLOWED_ATTR: [...HANDBOOK_POLICY_ATTR],
    ALLOW_DATA_ATTR: false,
    FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick'],
  });
}

/** After `escapeHtml` + `highlightMatch`, only `<mark>` should remain. */
export function sanitizeHandbookTitleHighlightHtml(html: string): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['mark'],
    ALLOWED_ATTR: [],
    ALLOW_DATA_ATTR: false,
  });
}

/** First word of a display name; used to derive the "@FirstName" plain-text token. */
function firstName(name: string): string {
  return (name || '').trim().split(/\s+/)[0] || 'user';
}

/**
 * Walk the editor DOM and emit a plain-text rendition with `@FirstName ` mention tokens
 * (matching the legacy textarea format) plus a `mentions[]` array of `startIndex/endIndex`
 * positions into that plain string.
 *
 * Why: the Slack/email mirror Cloud Function (`onTaskCommentCreated`) reads `comment.body`
 * as plain text and uses `mentions[].startIndex/endIndex` to splice in Slack handles. We
 * keep that contract intact even when the source is rich HTML.
 */
const PLAIN_ROMAN = [
  'i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x',
  'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi', 'xvii', 'xviii', 'xix', 'xx',
];

function plainAlpha(index: number): string {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(97 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** Nested lists use a subsection marker so plain text does not look like the list restarted. */
function plainListMarker(kind: 'ol' | 'ul', index: number, depth: number): string {
  if (kind === 'ul') return '- ';
  if (depth === 1) return `${plainAlpha(index)}. `;
  if (depth >= 2) return `${PLAIN_ROMAN[index] || String(index + 1)}. `;
  return `${index + 1}. `;
}

export function htmlToPlainWithMentions(html: string): { plain: string; mentions: CommentMention[] } {
  if (!html) return { plain: '', mentions: [] };

  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const root = tpl.content;

  const blocks: string[] = [];
  let current = '';
  const mentions: CommentMention[] = [];
  let runningOffset = 0;

  const flushBlock = () => {
    blocks.push(current);
    runningOffset += current.length + 2;
    current = '';
  };

  const isBlock = (el: Element) =>
    /^(P|DIV|H1|H2|H3|H4|H5|H6|BLOCKQUOTE|PRE|UL|OL|LI|HR)$/.test(el.tagName);

  const visit = (node: Node, listCtx?: { kind: 'ol' | 'ul'; index: number; depth: number }) => {
    if (node.nodeType === Node.TEXT_NODE) {
      current += node.textContent ?? '';
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;

    if (el.tagName === 'BR') {
      current += '\n';
      return;
    }

    if (el.tagName === 'HR') {
      if (current.length > 0) flushBlock();
      blocks.push('---');
      runningOffset += 4;
      return;
    }

    const hubHash = parseHubHashFromElement(el);
    if (hubHash) {
      current += hubHashToken(hubHash);
      return;
    }

    const structureMention = parseStructureMentionFromElement(el);
    if (structureMention) {
      current += buildStructureMentionToken({
        dealId: structureMention.dealId,
        structureId: structureMention.structureId,
        name: structureMention.label,
      });
      return;
    }

    if (el.hasAttribute('data-mention') || el.hasAttribute('data-user-id')) {
      const userId = el.getAttribute('data-user-id') || '';
      const labelAttr = el.getAttribute('data-label') || '';
      const text = labelAttr || (el.textContent ?? '').trim();
      const display = text.startsWith('@') ? text : `@${firstName(text.replace(/^@/, ''))}`;
      const start = runningOffset + current.length;
      current += display;
      const end = runningOffset + current.length - 1;
      current += ' ';
      if (userId) {
        mentions.push({ userId, userName: display.replace(/^@/, ''), startIndex: start, endIndex: end });
      }
      return;
    }

    if (el.tagName === 'A') {
      const href = el.getAttribute('href');
      const text = el.textContent ?? '';
      if (href && text && href !== text) {
        current += `${text} (${href})`;
      } else {
        current += text || href || '';
      }
      return;
    }

    if (el.tagName === 'IMG') {
      const alt = el.getAttribute('alt') || 'image';
      const src = el.getAttribute('src') || '';
      current += `[${alt}]${src ? `(${src})` : ''}`;
      return;
    }

    if (el.tagName === 'LI') {
      if (current.length > 0) flushBlock();
      const depth = listCtx?.depth ?? 0;
      const kind = listCtx?.kind ?? 'ul';
      const index = listCtx?.index ?? 0;
      current += `${'  '.repeat(depth)}${plainListMarker(kind, index, depth)}`;
      el.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          current += child.textContent ?? '';
          return;
        }
        if (child.nodeType !== Node.ELEMENT_NODE) return;
        const childEl = child as Element;
        if (childEl.tagName === 'P') {
          childEl.childNodes.forEach((inner) => visit(inner));
          return;
        }
        if (childEl.tagName === 'UL' || childEl.tagName === 'OL') {
          if (current.length > 0) flushBlock();
          visit(childEl, listCtx);
          return;
        }
        visit(child);
      });
      if (current.length > 0) flushBlock();
      return;
    }

    if (el.tagName === 'UL' || el.tagName === 'OL') {
      const kind = el.tagName === 'OL' ? 'ol' : 'ul';
      const depth = (listCtx?.depth ?? -1) + 1;
      const startAttr = Number.parseInt(el.getAttribute('start') || '1', 10);
      let i = Number.isFinite(startAttr) && startAttr > 0 ? startAttr - 1 : 0;
      el.childNodes.forEach((child) => {
        if ((child as Element).tagName === 'LI') {
          visit(child, { kind, index: i, depth });
          i += 1;
        } else {
          visit(child);
        }
      });
      return;
    }

    if (el.tagName === 'BLOCKQUOTE') {
      if (current.length > 0) flushBlock();
      const inner = (el.textContent ?? '').trim();
      const quoted = inner
        .split('\n')
        .map((line) => `> ${line}`)
        .join('\n');
      blocks.push(quoted);
      runningOffset += quoted.length + 2;
      return;
    }

    if (isBlock(el)) {
      if (current.length > 0) flushBlock();
      el.childNodes.forEach((child) => visit(child));
      if (current.length > 0) flushBlock();
      return;
    }

    el.childNodes.forEach((child) => visit(child));
  };

  root.childNodes.forEach((child) => visit(child));
  if (current.length > 0) flushBlock();

  const plain = blocks
    .map((b) => b.replace(/\s+$/g, ''))
    .filter((b) => b.length > 0)
    .join('\n\n')
    .trim();

  return { plain, mentions };
}

/**
 * Slack thread replies arrive as mrkdwn `<@U123>` / `<@U123|handle>`. Convert to plain `@…` tokens the
 * CRM UI can highlight, for legacy rows and edge-case text before server-side normalization.
 */
export function preprocessSlackMrkdwnUserMentionsInPlainBody(body: string): string {
  if (!body || !body.includes('<@')) return body;
  let s = body.replace(/<\s*@\s*([UW][A-Z0-9]+)\s*\|\s*([^>]*?)>/g, (_full, _sid: string, label: string) => {
    const h = String(label || '')
      .replace(/^@/, '')
      .trim()
      .split(/\s+/)[0] || 'user';
    const safe = h.replace(/[^A-Za-z0-9_.-]/g, '');
    return safe ? `@${safe}` : '@user';
  });
  s = s.replace(/<\s*@\s*([UW][A-Z0-9]+)\s*>/g, '@$1');
  return s;
}

/** For `String.prototype.split` — captures @mention chunks (CRM first names, Slack UIDs, dotted handles). */
export const PLAIN_COMMENT_MENTION_SPLIT_RE = /(@[A-Za-z0-9_.-]+)/;

/**
 * Upload a clipboard-pasted image as a comment inline asset and return its download URL.
 * Mirrors the auth + path conventions used by `uploadLegacyFiles` in TaskComments.tsx so
 * Storage rules apply identically.
 */
export async function uploadCommentInlineImage(
  file: File,
  taskId: string,
  onProgress?: (pct: number) => void,
): Promise<{ url: string; name: string; size: number; type: string; id: string }> {
  await auth.authStateReady();
  const firebaseUser = auth.currentUser;
  if (!firebaseUser || firebaseUser.isAnonymous) {
    throw new Error(
      'Firebase sign-in required for inline image uploads. Use your company Google or email sign-in.',
    );
  }

  await ensureCrmStorageAccessBeforeUpload();

  const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const objectName = buildCommentAttachmentStorageFileName(fileId, file.name || `paste.${(file.type || 'image/png').split('/')[1] || 'png'}`);
  const filePath = `task-comments/${taskId}/${objectName}`;
  const storageRef = ref(storage, filePath);
  const contentType = file.type || 'application/octet-stream';
  const task = uploadBytesResumable(storageRef, file, { contentType });

  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        const pct = snap.totalBytes > 0 ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0;
        onProgress?.(pct);
      },
      reject,
      () => resolve(),
    );
  });

  const url = await getDownloadURL(task.snapshot.ref);
  return { url, name: file.name || objectName, size: file.size, type: contentType, id: fileId };
}

/** Empty-state check that ignores TipTap's empty `<p></p>` document. */
export function isHtmlEmpty(html: string): boolean {
  if (!html) return true;
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const text = (tpl.content.textContent ?? '').trim();
  if (text.length > 0) return false;
  const hasMedia = !!tpl.content.querySelector(
    'img, hr, [data-mention], [data-board], [data-hub-link], .rce-board-mention, .rce-hub-hash-mention',
  );
  return !hasMedia;
}

export function shouldRenderLegacyBoardChip(comment: {
  inspirationBoard?: unknown;
  bodyHtml?: string;
  body?: string;
}): boolean {
  return Boolean(comment.inspirationBoard) && !commentHasInlineBoardMention(comment.bodyHtml, comment.body);
}

export function resolveInspirationBoardLink(input: {
  attachToggle: boolean;
  bodyHtml?: string;
  bodyPlain?: string;
  target: { leadId: string; dealId: string } | null;
}): { leadId: string; dealId: string } | null {
  if (!input.target) return null;
  if (input.attachToggle || commentHasInlineBoardMention(input.bodyHtml, input.bodyPlain)) {
    return input.target;
  }
  return null;
}
